import type { RunSummary } from '@shared/types/factory';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { runProgress, runRef, runsProgress } from './run-status';

export interface RunProgressBarProps {
  run: Pick<RunSummary, 'number' | 'status' | 'steps_done' | 'steps_total' | 'current_step' | 'current_step_name'>;
  /**
   * `bar` (default): the bar with its step and count above it, for the task detail. `stripe`: a thin
   * bar along the top edge of a Board card (the card needs `relative`), with the step as its hint.
   */
  variant?: 'bar' | 'stripe';
  className?: string;
}

/** How far a run has got through its flow's steps, coloured by its status. */
export function RunProgressBar({ run, variant = 'bar', className }: RunProgressBarProps) {
  const { percent, label, tone } = runProgress(run);
  const name = `${runRef(run.number)}: ${label}`;

  if (variant === 'stripe') {
    return (
      <div className={cn('absolute inset-x-0 top-0 overflow-hidden rounded-t-md', className)} data-hint={name}>
        <Progress value={percent} tone={tone} aria-label={name} className="h-1 rounded-none bg-muted" />
      </div>
    );
  }

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex justify-between gap-3 text-xs text-muted-foreground">
        <span className="min-w-0 truncate">{label}</span>
        {run.steps_total ? (
          <span className="shrink-0 tabular-nums">
            {Math.min(run.steps_done, run.steps_total)} of {run.steps_total} steps
          </span>
        ) : null}
      </div>
      <Progress value={percent} tone={tone} aria-label={name} />
    </div>
  );
}

type ProgressRun = RunProgressBarProps['run'];

/** SF-T-278: one run going on a parent's sub-task, by the sub-task's ref ("SF-T-71"). */
export interface SubtaskRunProgress {
  ref: string;
  run: ProgressRun;
}

/** "SF-T-71, R-91: Step 4 of 5 · Review; SF-T-72, R-92: …": each sub-task's run, for a hint. */
export function subtaskRunsSentence(runs: readonly SubtaskRunProgress[]): string {
  return runs.map(({ ref, run }) => `${ref}, ${runRef(run.number)}: ${runProgress(run).label}`).join('; ');
}

/**
 * SF-T-278: the runs going on a parent's sub-tasks, together, as a stripe along the top of its Board card (the
 * card needs `relative`): their mean progress, and each sub-task's run and step as its hint.
 */
export function SubtaskRunsProgressStripe({ runs, className }: { runs: readonly SubtaskRunProgress[]; className?: string }) {
  const total = runsProgress(runs.map((r) => r.run));
  if (!total) return null;
  const name = `Sub-tasks' runs, ${total.percent}% together: ${subtaskRunsSentence(runs)}`;
  return (
    <div className={cn('absolute inset-x-0 top-0 overflow-hidden rounded-t-md', className)} data-hint={name} data-testid="subtask-runs-progress">
      <Progress value={total.percent} tone={total.tone} aria-label={name} className="h-1 rounded-none bg-muted" />
    </div>
  );
}
